/* eslint-disable @typescript-eslint/naming-convention */
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface PaletteColorOptions {
    main: string;
    light?: string;
    dark?: string;
    contract?: string;
    disabled?: string;
    border?: string;
    focus?: string;
    drag?: string;
    hover?: string;
    activated?: string;
    selected?: string;
  }
  interface Palette {
    uxGrey: PaletteColorOptions;
    uxBlue: PaletteColorOptions;
    caution: PaletteColorOptions;
  }
  interface PaletteOptions {
    uxGrey: PaletteColorOptions;
    uxBlue: PaletteColorOptions;
    caution: PaletteColorOptions;
  }
  interface TypographyVariants {
    title: React.CSSProperties;
    subtitle: React.CSSProperties;
    bodyCallout: React.CSSProperties;
    bodyMediumEmphasis: React.CSSProperties;
    label1: React.CSSProperties;
    label2: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    title?: React.CSSProperties;
    subtitle?: React.CSSProperties;
    bodyCallout?: React.CSSProperties;
    bodyMediumEmphasis?: React.CSSProperties;
    label1?: React.CSSProperties;
    label2?: React.CSSProperties;
  }
}

// A custom theme for this app
const theme = createTheme({
  components: {
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontWeight: 400,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          lineHeight: 1.25,
          padding: '8px 16px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        standardError: {
          backgroundColor: '#ffefee',
          color: '#c70a00',
        },
        standardSuccess: {
          backgroundColor: '#eaf9e0',
          color: '#1f7a3c',
        },
        standardWarning: {
          backgroundColor: '#fff2e5',
          color: '#c63e15',
        },
        standardInfo: {
          backgroundColor: '#E1EAFB',
          color: '#2056BA',
        },
        icon: {
          marginRight: 6,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 24,
        },
      },
    },
  },
  //fonts following standards here: https://xd.adobe.com/view/105810a5-8cbd-4a22-8290-d863a16bdf34-450b/screen/fe693e1e-43ab-41dd-af3c-766ad9a50e99/specs/
  typography: {
    h1: {
      font: 'normal normal 300 60px/72px Roboto',
      letterSpacing: 0.12,
      fontWeight: 300,
      fontSize: 60,
    },
    h2: {
      font: 'normal normal normal 49px/59px Roboto',
      letterSpacing: 0,
      fontWeight: 400,
      fontSize: 49,
    },
    h3: {
      font: 'normal normal normal 42px/51px Roboto',
      fontWeight: 400,
      fontSize: 42,
    },
    h4: {
      font: 'normal normal normal 37px/44px Roboto',
      letterSpacing: 0,
      fontWeight: 400,
      fontSize: 37,
    },
    h5: {
      font: 'normal normal normal 32px/38px Roboto',
      letterSpacing: 0,
      fontWeight: 400,
      fontSize: 32,
    },
    h6: {
      font: 'normal normal normal 28px/34px Roboto',
      letterSpacing: 0,
      fontWeight: 400,
      fontSize: 28,
    },
    title: {
      font: 'normal normal 500 21px/25px Roboto',
      letterSpacing: 0,
      fontWeight: 500,
      fontSize: 21,
    },
    subtitle: {
      font: 'normal normal 500 19px/23px Roboto',
      letterSpacing: 0,
      fontWeight: 500,
      fontSize: 19,
    },
    bodyCallout: {
      font: 'normal normal 500 16px/19px Roboto',
      letterSpacing: 0,
      fontWeight: 500,
      fontSize: 16,
    },
    body1: {
      font: 'normal normal 400 16px/19px Roboto',
      letterSpacing: 0,
      fontWeight: 400,
      fontSize: 16,
    },
    bodyMediumEmphasis: {
      font: 'normal normal 400 16px/19px Roboto',
      letterSpacing: 0,
      fontWeight: 400,
      fontSize: 16,
      color: '#00000099',
    },
    button: {
      font: 'normal normal 500 14px/17px Roboto',
      letterSpacing: 1.25,
      fontWeight: 500,
      fontSize: 14,
    },
    label1: {
      font: 'normal normal 500 12px/14px Roboto',
      letterSpacing: 1.2,
      fontWeight: 500,
      fontSize: 12,
    },
    label2: {
      font: 'normal normal normal 12px/14px Roboto',
      letterSpacing: 0,
      fontWeight: 400,
      fontSize: 12,
    },
  },
  palette: {
    primary: {
      main: '#276EF1',
    },
    secondary: {
      main: '#2056ba',
    },
    error: {
      main: '#c70a00',
      light: '#FFEFEE',
    },
    warning: {
      main: '#c63e15',
      light: '#FFF7EE',
    },
    success: {
      main: '#1F7A3C',
      dark: '#34C759',
      light: '#EAF9E0',
    },
    background: {
      default: '#FAFAFF',
    },
    uxGrey: {
      main: '#757575',
      border: '#CCCCCC',
      dark: '#212121',
      disabled: '#9F9F9F',
      hover: '#F5F5F5',
      drag: '#EBEBEB',
      focus: '#E0E0E0',
    },
    uxBlue: {
      main: '#276EF1',
      activated: '#E4EDFE',
      selected: '#EEF4FE',
    },
    caution: {
      main: '#FF952A',
      light: '#FBEEE5',
      dark: '#ef6c00',
    },
  },
  spacing: 8,
});

//declaring new typography variants
declare module '@mui/material/styles' {
  interface TypographyVariants {
    title: React.CSSProperties;
    subtitle: React.CSSProperties;
    bodyCallout: React.CSSProperties;
    bodyMediumEmphasis: React.CSSProperties;
    label1: React.CSSProperties;
    label2: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    title?: React.CSSProperties;
    subtitle?: React.CSSProperties;
    bodyCallout?: React.CSSProperties;
    bodyMediumEmphasis?: React.CSSProperties;
    label1?: React.CSSProperties;
    label2?: React.CSSProperties;
  }
}
// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    title: true;
    subtitle: true;
    bodyCallout: true;
    bodyMediumEmphasis: true;
    label1: true;
    label2: true;
  }
}

export default theme;
